<template>
  <div ref="searchRef" :class="rootClass" :style="rootStyle">
    <AdaptiveImage
      class="search-topBackground"
      :field="fields?.topBackground"
      :isBackground="true"
      :lazy-load="false"
      :enableScrollDetection="false"
      :show="true"
    />
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="search-bar" v-if="!isLoading">
        <JssRichText class="search-bar-title" :field="fields?.searchTitle" />
        <div class="search-bar-input-wrap">
          <input
            type="text"
            class="search-bar-input"
            v-model="searchKeyword"
            :placeholder="fields?.searchPlaceholder?.value"
            @keyup.enter="handleSearch"
          />
          <SiteButton
            class="search-bar-button"
            v-bind="fields?.searchButton"
            @click="handleSearch"
          />
        </div>
      </div>
      <div class="search-loading" v-if="isLoading">
        <div class="search-loading-spinner"></div>
      </div>
      <div class="search-result" v-if="!isLoading">
        <div class="search-result-lt">
          <h3 class="search-result-lt-title" v-if="fields?.resultTitle">
            <JssRichText :field="fields?.resultTitle" tag="strong" />
            <span class="search-result-lt-title-count">({{ totalCount }})</span>
          </h3>
          <div class="search-result-lt-menu">
            <div
              class="search-result-lt-menu-item"
              v-for="item in menuData"
              :key="item?.id"
              :class="{ active: item?.code === currentType }"
              @click="handleMenuClick(item)"
            >
              <span>{{ item?.title }}</span>
              <span>({{ item?.count }})</span>
            </div>
          </div>
        </div>
        <div class="search-result-rt">
          <template v-if="displayContent.length > 0">
            <div class="search-result-rt-content">
              <a
                class="search-result-rt-content-item"
                v-for="item in displayContent"
                :key="item?.id"
                :href="item?.url"
                target="_blank"
              >
                <div class="search-result-rt-content-item-link">
                  <JssRichText
                    class="search-result-rt-content-item-title"
                    :field="{ value: item?.title }"
                    tag="p"
                  />
                  <JssRichText
                    class="search-result-rt-content-item-desc"
                    :field="{ value: item?.description }"
                    tag="p"
                  />
                </div>
                <SiteButton
                  class="search-result-rt-content-item-button"
                  v-bind="fields?.moreButton"
                />
              </a>
            </div>
            <Pagination
              v-model:currentPage="currentPage"
              :totalPages="totalPages"
              v-if="totalPages > 1"
            />
          </template>
          <JssRichText
            v-else
            class="search-result-rt-content-empty"
            :field="fields?.noResultTitle"
          />
        </div>
      </div>
    </ContentGrid>
    <AdaptiveImage
      class="search-bottomBackground"
      :field="fields?.bottomBackground"
      :isBackground="true"
      :lazy-load="false"
      :enableScrollDetection="false"
      :show="true"
    />
  </div>
</template>

<script>
import { computed, nextTick, onUnmounted, reactive, ref, watch, onMounted, toRefs } from 'vue';
import utils from '@/utils';
import api from '@/api/index';
import { useRoute } from 'vue-router';

export default {
  name: 'Search',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const searchRef = ref(null);
    const route = useRoute();
    const currentPage = ref(1);
    const currentMenuIndex = ref(1);
    const currentType = ref('');
    const searchKeyword = ref('');
    // Pre-fill keyword from URL query (e.g. passed from Header search)
    const queryKeyword = route.query?.q;
    if (typeof queryKeyword === 'string' && queryKeyword) {
      searchKeyword.value = queryKeyword;
    }
    const pageSize = computed(() => {
      const size = parseInt(props.fields?.pageSize?.value);
      return isNaN(size) ? 10 : size;
    });

    const state = reactive({
      isLoading: false,
      searchData: null,
    });

    const isMenuClick = ref(false);

    const fetchSearchData = async () => {
      state.isLoading = true;
      try {
        const [res, error] = await api.search.getSearch({
          q: searchKeyword.value,
          page: currentPage.value,
          limit: pageSize.value,
          type: currentType.value,
        });
        if (res) {
          state.searchData = res;
        }
      } catch (e) {
        // search failed; empty content list will trigger noResultTitle display
      } finally {
        state.isLoading = false;
      }
    };

    const initDefaultMenu = () => {
      currentMenuIndex.value = '';
      currentType.value = '';
    };

    onMounted(() => {
      initDefaultMenu();
      fetchSearchData();
      nextTick(() => {
        updateMinHeight();
        window.addEventListener('resize', updateMinHeight);
      });
    });

    onUnmounted(() => {
      window.removeEventListener('resize', updateMinHeight);
    });

    const menuData = computed(() => {
      const searchData = state.searchData;
      const menuItems = [];

      if (searchData) {
        Object.keys(searchData).forEach((key) => {
          const item = searchData[key];
          menuItems.push({
            id: key,
            title: key.charAt(0).toUpperCase() + key.slice(1),
            code: key,
            count: item?.count || 0,
          });
        });
      }

      return menuItems;
    });

    const totalCount = computed(() => {
      const searchData = state.searchData;
      if (!searchData) return 0;
      return (searchData.content?.count || 0) + (searchData.webinar?.count || 0);
    });

    const filteredContent = computed(() => {
      const searchData = state.searchData;
      if (!searchData) return [];

      const selectedMenu = menuData.value.find((item) => item.id === currentMenuIndex.value);
      const selectedCode = selectedMenu?.code;

      let list = [];
      if (!selectedCode) {
        const contentList = searchData.content?.list || [];
        const webinarList = searchData.webinar?.list || [];
        list = [...contentList, ...webinarList];
      } else if (selectedCode === 'content') {
        list = searchData.content?.list || [];
      } else if (selectedCode === 'webinar') {
        list = searchData.webinar?.list || [];
      }

      return list;
    });

    const totalPages = computed(() => {
      const searchData = state.searchData;
      if (!searchData) return 0;

      let totalCount = 0;
      if (currentType.value === 'content') {
        totalCount = searchData.content?.count || 0;
      } else if (currentType.value === 'webinar') {
        totalCount = searchData.webinar?.count || 0;
      } else {
        totalCount = (searchData.content?.count || 0) + (searchData.webinar?.count || 0);
      }

      return Math.max(1, Math.ceil(totalCount / pageSize.value));
    });

    const displayContent = computed(() => {
      return filteredContent.value;
    });

    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'search'));

    const minHeight = ref(null);

    const updateMinHeight = () => {
      if (!utils.dom.canUseDOM()) return;
      const header = document.querySelector('.c-gen-nav');
      const footer = document.querySelector('.c-footer');
      const headerH = header ? header.offsetHeight : 0;
      const footerH = footer ? footer.offsetHeight : 0;
      minHeight.value = `calc(100vh - ${headerH}px - ${footerH}px)`;
    };

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return { ...result, minHeight: minHeight.value };
    });

    const handleMenuClick = (item) => {
      isMenuClick.value = true;
      currentMenuIndex.value = item.id;
      currentType.value = item.code;
      currentPage.value = 1;
      fetchSearchData();
      isMenuClick.value = false;
    };

    watch(currentPage, () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (!isMenuClick.value) {
        fetchSearchData();
      }
    });

    const handleSearch = () => {
      currentPage.value = 1;
      fetchSearchData();
    };

    return {
      rootClass,
      rootStyle,
      searchRef,
      displayContent,
      totalPages,
      currentPage,
      currentMenuIndex,
      currentType,
      menuData,
      totalCount,
      handleMenuClick,
      handleSearch,
      searchKeyword,
      ...toRefs(state),
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.search {
  position: relative;
  width: 100%;
  padding: 40px 0;

  .e-content-grid {
    position: relative;
    z-index: 10;
  }

  .pagination {
    position: relative;
    z-index: 10;
  }

  &-topBackground {
    width: 323px;
    height: 261px;
    position: absolute;
    top: 0;
    left: 0;
  }

  &-bottomBackground {
    width: 786px;
    height: 609px;
    position: absolute;
    bottom: 0;
    right: 0;
  }

  &-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px 0;

    &-spinner {
      width: 40px;
      height: 40px;
      border: 4px solid $neutral-disable-surface;
      border-top-color: $blue-night;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  }

  &-bar {
    width: 100%;
    position: relative;
    z-index: 10;

    &-title {
      font-size: 24px;
      line-height: 1.4;
      color: $grey-black;
      font-weight: 600;
    }

    &-input-wrap {
      display: flex;
      gap: 16px;
      width: 100%;
      margin-top: 16px;

      input {
        flex: 1;
        height: 40px;
        padding: 0 16px;
        border: 1px solid #b2b2b2;
        border-radius: 4px 0 0 4px;
        font-size: 14px;
        line-height: 1.4;
        color: $black;
        outline: none;
        background: $white;

        &::placeholder {
          color: rgba($black, 0.3);
          font-size: 16px;
        }

        &:focus {
          border-color: $blue-night;
        }
      }

      .e-site-button {
        border-radius: 0 4px 4px 0;
        padding: 0 24px;
        height: 40px;
        min-width: auto;
      }
    }
  }

  &-result {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 24px;
    position: relative;
    z-index: 10;

    &-lt {
      width: 100%;
      position: sticky;
      top: 20px;
      left: 0;
      z-index: 1;
      background: $white;

      &-title {
        font-size: 16px;
        line-height: 1.4;
        color: $black;
        display: flex;
        align-items: center;
        gap: 8px;

        &-count {
          font-size: 14px;
          color: $grey-black;
        }
      }

      &-menu {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: 16px;

        &-item {
          font-size: 14px;
          line-height: 1.4;
          color: $grey-black;
          padding: 14px;
          border: 1px solid $neutral-disable-surface;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;

          &.active {
            background: $blue-night;
            color: $white;
          }
        }
      }
    }

    &-rt {
      width: 100%;

      &-content {
        display: flex;
        flex-direction: column;
        gap: 12px;

        &-item {
          padding: 16px;
          border: 1px solid $neutral-disable-surface;
          border-radius: 10px;

          &-link {
            text-decoration: none;
            color: inherit;
          }

          &-title {
            font-size: 16px;
            line-height: 1.2;
            font-weight: 600;
            color: $grey-black;
          }

          &-desc {
            font-size: 13px;
            line-height: 1.4;
            color: #656565;
            margin-top: 8px;
          }

          &-button {
            margin-top: 12px;
          }
        }

        &-loading,
        &-noMore {
          padding: 16px;
          text-align: center;
          font-size: 14px;
          color: #656565;
        }

        &-empty {
          font-size: 16px;
          color: #606575;
          line-height: 1.2;
          text-align: center;
          border-radius: 8px;
          background-color: #f7f8f7;
          padding: 16px;
        }
      }
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-bar {
      &-title {
        font-size: 18px;
        margin-bottom: 20px;
      }

      &-input-wrap {
        input {
          height: 48px;
          font-size: 16px;
        }

        .e-site-button {
          height: 48px;
          padding: 0 32px;
        }
      }
    }

    &-result {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: center;
      gap: 32px;

      &-lt {
        width: grid-width(4.67);
        position: sticky;
        top: 100px;
        left: 0;
        z-index: 1;
        align-self: flex-start;
        max-height: calc(100vh - 40px);
        overflow-y: auto;
        background: transparent;

        &-title {
          font-size: 16px;
          line-height: 1.4;
          color: $black;
        }

        &-menu {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;

          &-item {
            font-size: 14px;
            line-height: 1.4;
            color: $grey-black;
            padding: 17px;
            border: 1px solid $neutral-disable-surface;
            border-radius: 4px;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;

            &.active {
              background: $blue-night;
              color: $white;
            }
          }
        }
      }
      &-rt {
        width: calc(100% - grid-width(4.67) - 32px);
        &-content {
          display: flex;
          flex-direction: column;
          gap: 16px;

          &-item {
            padding: 24px;
            border: 1px solid $neutral-disable-surface;
            border-radius: 14px;

            &-link {
              text-decoration: none;
              color: inherit;
            }

            &-title {
              font-size: 18px;
              line-height: 1.2;
              font-weight: 600;
              color: $grey-black;
            }

            &-desc {
              font-size: 14px;
              line-height: 1.4;
              color: #656565;
              margin-top: 12px;
            }
          }

          &-empty {
            padding: 32px;
          }
        }
      }
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
